import Link from 'next/link';
import { useRef } from 'react';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import truncate from 'lodash/truncate';
import styles from '@components/SingleArticleInspirations/SingleArticleInspirations.styles';
import Heading from '@components/Heading/Heading';
import { ICmsDocument } from '@features/cms/typescript';
import ProjectFeatures from '@components/ProjectFeatures/ProjectFeatures';
import VoxboxWidget from '@features/cms/components/collections/VoxboxWidget/VoxboxWidget';
import { getSizesString } from '@utils/misc';
import getImageQuality from '@utils/getImageQuality';

type PropsType = {
  article: ICmsDocument;
  isInterior: boolean;
  buttonHistoryBack?: boolean;
  onClick?: () => void;
  onAuxClick?: () => void;
};

const SingleArticleInspirations = ({
  article,
  isInterior,
  buttonHistoryBack = false,
  onClick = undefined,
  onAuxClick = undefined,
}: PropsType) => {
  const hoverRef = useRef<HTMLDivElement | null>(null);
  const { baseData } = article;
  const { t } = useTranslation('common');
  const handleButtonHistoryBack = () => (buttonHistoryBack ? '?button-history-back' : '');

  return (
    <div sx={styles.singleArticle(isInterior)} ref={hoverRef}>
      {baseData && (
        <>
          {isInterior && (
            <VoxboxWidget variant="dark" hoverRef={hoverRef} small url={baseData.projectUrl || undefined} />
          )}
          <Link
            href={`/inspirations/${isInterior ? 'interior-designs' : 'ideas'}/${
              baseData.slug
            }${handleButtonHistoryBack()}`}
            onClick={onClick}
            onAuxClick={onAuxClick}
          >
            <div sx={styles.wrapper}>
              <div sx={styles.imgContainer(isInterior)}>
                <Image
                  src={baseData.image || ''}
                  alt={baseData.name || ''}
                  quality={getImageQuality()}
                  sizes={getSizesString(['100vw', '100vw', '50vw', '25vw'])}
                  fill
                />
                {isInterior && baseData?.imageHover && (
                  <Image
                    sx={styles.imgContainer(isInterior).imageHover}
                    className="image_hover"
                    quality={getImageQuality()}
                    src={baseData.imageHover || ''}
                    alt={baseData.name || ''}
                    sizes={getSizesString(['100vw', '100vw', '50vw', '25vw'])}
                    fill
                  />
                )}
              </div>
              <div sx={styles.headingContainer}>
                <Heading addSx={styles.heading} h={4} headingType={4}>
                  {baseData.name}
                </Heading>
                {baseData?.tags && (
                  <div sx={styles.tag}>
                    {Object.values(baseData.tags).map((tag) => (
                      <span key={tag}>{`#${tag}`}</span>
                    ))}
                  </div>
                )}
              </div>
              {isInterior ? (
                <ProjectFeatures
                  surface={baseData.surface}
                  furniture={baseData.furniture}
                  doors={baseData.doors}
                  floor={baseData.floor}
                  walls={baseData.walls}
                  rows={false}
                  isListing
                />
              ) : (
                <div sx={styles.description}>
                  {truncate(baseData.description || '', { length: 140, omission: '...', separator: ' ' })}
                </div>
              )}
              <div sx={styles.readMore} className="readMore">
                {isInterior ? t('readMoreInterior') : t('readMore')}
              </div>
            </div>
          </Link>
        </>
      )}
    </div>
  );
};

export default SingleArticleInspirations;
